import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { TableCell, TableRow, Checkbox, Collapse } from '@mui/material';
import ConfirmDialog from '@/components/UI/Dialog/ConfirmDialog';
import TooltipComponent from '@/components/UI/Tooltip/TooltipComponent';
import useGlobalSpinnerContext from '@/hooks/useGlobalSpinner';
import useToastContext from '@/hooks/useToastContext';
import { setAffectedVehiclesSeries } from '@/redux/Campaign/campaign.action';
import { getVolumeHeaderPlants, getCampaignVehicles, getCampaignVehiclesVolumes } from '@/redux/Campaign/campaign.reducer';
import affectedVehicleServices from '@/services/affectedVehicleService';
import { ObjLimitationSerie, Volume, WKZNamePlant } from '@/types';
import ChipsMultiSelector from './ChipsMultiSelector/ChipsMultiSelector';
import PlantChips from './PlantChips/PlantChips';
import { plantsHaveFiles, mapPlantsWithfiles } from './series-row-utils';
import styles from './SeriesRow.module.scss';
import { uniquePlants } from './utils/plants.utils';
import { bodyTypesWithPlants, removeKey, getTypeVersions } from './utils/type-versions.utils';
import SeriesDropdown from '../SeriesDropdown/SeriesDropdown';

type SeriesRowProps = {
  selection: ObjLimitationSerie;
  onRowUpdate: (update: ObjLimitationSerie[]) => void; // eslint-disable-line no-unused-vars
  modelSeries: string[];
  seriesSelected: ObjLimitationSerie[];
  checkEnabled: boolean;
  isEditable: boolean;
  volumes: Volume[];
  headerPlants: WKZNamePlant;
  setSeries: (series: ObjLimitationSerie[]) => void; // eslint-disable-line no-unused-vars
  handleDeleteSeries: (serie: string) => void; // eslint-disable-line no-unused-vars
  index: number;
};

type DialogProps = {
  title: string;
  message: string;
  open: boolean;
  plantsToDelete?: WKZNamePlant[];
  plantCKDToDelete?: WKZNamePlant;
  series: ObjLimitationSerie[];
};

const EMPTY_DIALOG_PROPS = {
  title: '',
  message: '',
  open: false,
  plantsToDelete: [],
  series: [],
  plantCKDToDelete: undefined,
};

const SeriesRow: React.FC<SeriesRowProps> = (props) => {
  const { selection, onRowUpdate = () => { }, modelSeries = [], seriesSelected, checkEnabled, isEditable,
    volumes, headerPlants, setSeries, handleDeleteSeries, index } = props;
  const [bodyTypes, setBodyTypes] = useState({});
  const [dialogProps, setDialogProps] = useState<DialogProps>(EMPTY_DIALOG_PROPS);
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { addToast } = useToastContext();
  const { setLoading } = useGlobalSpinnerContext();

  const handleRowUpdate = (update) => {
    onRowUpdate({ ...selection, ...update });

    if (update.typeVersions) {
      let newSerie = {};
      let newStateSeries: ObjLimitationSerie[] = [];
      const plants: WKZNamePlant[] = [];

      newSerie = {
        serie: selection.serie === null || selection.serie === '' ? update.serie : selection.serie,
        checked: false,
        typeVersions: update.typeVersions,
      };

      if (seriesSelected.length > 0 && (selection.serie === null || selection.serie === '')) {
        seriesSelected.forEach((current) => {
          if (current.serie !== '') {
            newStateSeries.push(current);
          }
        });
        newStateSeries.push(newSerie as ObjLimitationSerie);
      } else if (selection.serie !== null) {
        const foundSerie = seriesSelected.find((s) => s.serie === selection.serie);

        if (foundSerie) {
          foundSerie.typeVersions = update.typeVersions;
          foundSerie.serie = update.serie === undefined ? selection.serie : update.serie;
        }
        newStateSeries = seriesSelected.filter((row) => row.serie !== null);
      } else {
        newStateSeries.push(newSerie as ObjLimitationSerie);
      }

      newStateSeries.forEach((current) => {
        const plantsUnique: WKZNamePlant[] = uniquePlants(current.typeVersions);
        plantsUnique.forEach((plant) => {
          plants.push(plant);
        });
      });

      setSeries(newStateSeries);
    }
  };

  useEffect(() => {
    if (selection.serie) {
      affectedVehicleServices
        .getPlantsByBodyTypes(selection.serie.toUpperCase())
        .then((plantsByBodyTypes) => {
          setBodyTypes(plantsByBodyTypes.values);
        }).catch((error) => addToast(error.message, 'ERROR'));
    }
  }, [selection.serie]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
  }, [selection.typeVersions]);

  const getSelectedModelSeriesCodes = () => seriesSelected.map(({ serie }) => serie);

  const filterModelSeries = () => modelSeries.filter((model) => !getSelectedModelSeriesCodes().includes(model));

  const selectedSerieExists = (serie) => serie !== null && filterModelSeries().includes(serie)
    && !getSelectedModelSeriesCodes().includes(serie);

  const checkPlantsFilesAndSave = (typeVersions, serie?: string) => {
    const newSeries = seriesSelected.map((row) => (row.serie === selection.serie ? { ...selection, typeVersions } : row));
    const plantsWithFiles = plantsHaveFiles(newSeries, volumes, headerPlants, false);

    if (plantsWithFiles.length > 0) {
      setDialogProps({
        ...dialogProps,
        open: true,
        plantsToDelete: plantsWithFiles,
        series: newSeries,
        title: t('affectedVehicles.dialog.title'),
        message: t('affectedVehicles.dialog.message', { x: mapPlantsWithfiles(plantsWithFiles) }),
      });
    } else {
      handleRowUpdate(serie ? { serie, typeVersions } : { typeVersions });
    }
  };

  const handleModelSerieChange = (serie: string | null) => {
    if (serie === null) {
      handleDeleteSeries(selection.serie);
    } else if (selectedSerieExists(serie)) {
      setLoading(true);

      affectedVehicleServices
        .getPlantsByBodyTypes(serie.toUpperCase())
        .then((plantsByBodyTypes) => {
          const typeVersions = plantsByBodyTypes.values;
          setBodyTypes(typeVersions);
          checkPlantsFilesAndSave(typeVersions, serie);
        }).catch((error) => addToast(error.message, 'ERROR'))
        .finally(() => setLoading(false));
    }
  };

  const handleConfirmPlantDeletion = (chipToDelete: string) => {
    const plantToDelete = uniquePlants(selection.typeVersions).find(({ wkzNumber }) => wkzNumber === chipToDelete);

    if (plantToDelete.ckd) {
      setDialogProps({
        ...dialogProps,
        open: true,
        title: t('affectedVehicles.dialogCKD.title'),
        message: t('affectedVehicles.dialogCKD.message'),
        plantCKDToDelete: plantToDelete,
      });
    } else {
      deletePlant(plantToDelete);
    }
  };

  const deletePlant = (plantToDelete: WKZNamePlant) => {
    const selectedPlants = uniquePlants(selection.typeVersions).filter(({ wkzNumber }) => wkzNumber === plantToDelete.wkzNumber);
    const typeVersions = bodyTypesWithPlants(selectedPlants, selection.typeVersions);
    checkPlantsFilesAndSave(typeVersions);
  };

  const handleTypeVersionsSelectionChange = (typeVersionsIds: string[]) => {
    const typeVersions = getTypeVersions(typeVersionsIds, bodyTypes);
    checkPlantsFilesAndSave(typeVersions);
  };

  const handleRemove = (typeVersionId: string) => {
    const typeVersions = removeKey(selection.typeVersions, typeVersionId);
    checkPlantsFilesAndSave(typeVersions);
  };

  const confirmDeletePlants = () => {
    if (dialogProps.plantCKDToDelete) {
      deletePlant(dialogProps.plantCKDToDelete);
    } else if (dialogProps.plantsToDelete) {
      setSeries(dialogProps.series);
    }
    setDialogProps(EMPTY_DIALOG_PROPS);
  };

  const collapsableTypeVersions = (plantName: string) => {
    // Transforms { FC: [{ plantName: 'Tuscaloosa' }], FV: [{ plantName: 'Tuscaloosa' }, { plantName: 'Bremen' }]}
    // to ['FC', 'FV'] for "Tuscaloosa", and to ['FV'] for "Bremen"
    const versions = Object.keys(selection.typeVersions).filter((typeVersion) => (
      selection.typeVersions[typeVersion].some((version) => version.plantName === plantName)
    ));

    return versions.join(', ');
  };

  return (
    <>
      <TableRow className={styles.TableBodyRow}>
        {isEditable && (
          <TableCell padding="checkbox">
            <Checkbox
              key={`${selection.serie}-${Math.random()}`}
              color="primary"
              checked={selection.checked}
              onClick={() => handleRowUpdate({ checked: !selection.checked })}
            />
          </TableCell>
        )}
        <TableCell>
          <TooltipComponent title={t('affectedVehicles.arrowCollapsedModelSerie')}>
            <i className={`di ${open ? 'icon-pfeil-chevron-unten' : 'icon-pfeil-chevron-rechts'}`} onClick={(e) => { e.stopPropagation(); setOpen(!open); }} />
          </TooltipComponent>
        </TableCell>
        <TableCell className={styles.Cell} align="left">
          <SeriesDropdown
            checkEnabled={checkEnabled}
            handleChange={(event, value) => handleModelSerieChange(value)}
            id={`series-model-serie-selector[${index}]`}
            fieldPath={['affectedVehicles', `series[${index}]`, 'modelSerie']}
            options={[...filterModelSeries(), ...[selection.serie ? [selection.serie] : []]].flat()}
            value={selection.serie}
            placeHolder={t('affectedVehicles.modelSeries')}
          />
        </TableCell>
        <TableCell className={styles.Cell} align="left">
          <ChipsMultiSelector
            checkEnabled={checkEnabled}
            formControlClass={styles.FullWidth}
            fieldPath={['affectedVehicles', `series[${index}]`, 'typeVersions']}
            onSelectionChange={(ev) => handleTypeVersionsSelectionChange(ev.target.value as string[])}
            onDelete={(deletedItem) => handleRemove(deletedItem)}
            id={`series-body-types-selector[${index}]`}
            options={Object.keys(bodyTypes || {})}
            value={Object.keys(selection.typeVersions || {})}
          />
        </TableCell>
        <TableCell className={styles.Cell} align="left">
          <PlantChips
            checkEnabled={checkEnabled}
            list={uniquePlants(selection.typeVersions)}
            onChange={handleConfirmPlantDeletion}
            fieldPath={['affectedVehicles', `series[${index}]`, 'typeVersions', 'plants']}
          />
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingLeft: 4, paddingTop: 0, paddingRight: 0 }} colSpan={6}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            {uniquePlants(selection.typeVersions).map(({ plantName }) => (
              <TableRow key={plantName}>
                <TableCell style={{ width: '2%' }} />
                <TableCell style={{ width: '7%' }} />
                <TableCell style={{ width: '17%' }} />
                <TableCell style={{ width: '50%' }} align="center">
                  {collapsableTypeVersions(plantName)}
                </TableCell>
                <TableCell style={{ width: '40%' }} align="center">
                  {plantName}
                </TableCell>
              </TableRow>
            ))}
          </Collapse>
        </TableCell>
      </TableRow>

      {dialogProps.open && (
        <ConfirmDialog
          close={() => setDialogProps(EMPTY_DIALOG_PROPS)}
          confirm={confirmDeletePlants}
          title={dialogProps.title}
          message={dialogProps.message}
          closeBtnText={t('dialog.cancel') || ''}
          confirmBtnText={t('dialog.confirm') || ''}
        />
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  headerPlants: getVolumeHeaderPlants(state) as unknown as WKZNamePlant,
  seriesSelected: getCampaignVehicles(state).series || [],
  volumes: getCampaignVehiclesVolumes(state),
});

const actionCreators = {
  setSeries: setAffectedVehiclesSeries,
};

export default connect(mapStateToProps, actionCreators)(SeriesRow);
